import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { selectGridSelection } from 'app/components/Common/Grid/GridSelection/GridSelection.selector';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { GridColumns } from 'app/models/GlobalSearch/GlobalSearch';
import _ from 'lodash';
import { selectUser } from 'oidc/user.selectors';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import JobPreferencesModal from '../JobPreferencesModal';
import { getJobPrefercesGridColumns, getJobPreferencesGridVisibilityModel } from './JobPreferencesGridHelpers';
import { selectJobPreferencesData } from '../store/JobPreferences.selectors';
import { jobPreferncesActions } from '../store/JobPreferences.redux';
import { useParams } from 'react-router-dom';
import { RouteParams } from '../../../Profile/CandidateProfile';
import { deleteCandidateJobPreferences } from 'app/services/CandidateServices/CandidateServices';
import { PreferenceDeleteDialog } from './PreferenceDeletePopup/PreferenceDeletePopup';
import { gridSelectionActions } from 'app/components/Common/Grid/GridSelection/GridSelection.redux';
import { GridSortModel, GridCallbackDetails } from '@mui/x-data-grid-pro';
import { Authorized } from 'oidc/userHelper';
import { userRoles } from 'oidc/userRoles';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import { taskDetailsActions } from '@AMIEWEB/Tasks/store/Tasks.redux';

export const JobPreferenceGrid = () => {
  const params = useParams<RouteParams>();
  const { t } = useTranslation();
  const [tableColumns, setTableColumns] = React.useState<any[]>(getJobPrefercesGridColumns(t));
  const [tableData, setTableData] = React.useState<any[]>([]);
  const [open, setOpen] = React.useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState<boolean>(false);
  const user = useSelector(selectUser);
  const { selectedData } = useSelector(selectGridSelection);
  const { jobPreferencesGridData, loading } = useSelector(selectJobPreferencesData);
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();

  const [defaultColumns, setDefaultColumns] = React.useState<any[]>([...getJobPrefercesGridColumns(t)]);
  const [defaultColumnVisibilityModel, setDefaultColumnVisibilityModel] = React.useState<any>(
    _.cloneDeep(getJobPreferencesGridVisibilityModel),
  );
  const [defaultSortModel, setDefaultSortModel] = React.useState<any[]>([]);

  const [currentColumns, setCurrentColumns] = React.useState<any[]>([...getJobPrefercesGridColumns(t)]);
  const [currentColumnVisibilityModel, setCurrentColumnVisibilityModel] = React.useState<any>(
    _.cloneDeep(getJobPreferencesGridVisibilityModel),
  );
  const [currentSortModel, setCurrentSortModel] = React.useState<any[]>([]);

  const onSortModelChanged = (model: GridSortModel, details: GridCallbackDetails) => {
    setCurrentSortModel(model);
  };

  const handleResetColumns = () => {
    setCurrentColumns([...getJobPrefercesGridColumns(t)]);
    setCurrentColumnVisibilityModel(_.cloneDeep(getJobPreferencesGridVisibilityModel));
    setCurrentSortModel([]);
  };

  const [isDeleteInProgress, setIsDeleteInProgress] = React.useState(false);
  React.useEffect(() => {
    setTableData(jobPreferencesGridData.slice().reverse());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPreferencesGridData]);

  React.useEffect(() => {
    dispatch(
      jobPreferncesActions.getJobPreferencesAction({
        travelerId: parseInt(params.travelerId),
        brandId: parseInt(params.brandId),
      }),
    );
  }, [params.travelerId,params.brandId]);

  const enable = React.useCallback(
    button => {
      if (
        Authorized(
          [userRoles.recruitment_Leadership, userRoles.recruitment_TeamMember],
          user.userInfo,
        )
      ) {
        if (button === 'addPreferences') {
          return loading;
        } else if (selectedData.length !== 0 && !loading) {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    },
    [loading, selectedData.length, user.userInfo],
  );

  const handleOpenModal = () => {
    setOpen(true);
  };

  const deleteJobPreferences = async () => {
    setIsDeleteInProgress(true);
    const requestBody = {
      candidateId: params.travelerId,
      brandId: params.brandId,
      ids: selectedData,
    };
    if (selectedData) {
      try {
        const response = await deleteCandidateJobPreferences(requestBody);
        setOpenDeleteDialog(false);
        if (response.status === 200) {
          dispatch(
            globalActions.setSnackBar({
              message: t('candidate.jobPreferences.deleteOrderPreference'),
              severity: 'success',
            }),
          );
          dispatch(
            gridSelectionActions.setGridSelections({
              selectedData: [],
              isCompareGridOpen: false,
            }),
          );
          dispatch(
            jobPreferncesActions.getJobPreferencesAction({
              travelerId: parseInt(params.travelerId),
              brandId: parseInt(params.brandId),
            }),
          );
          setIsDeleteInProgress(false);
          //*Getting the new notification counts
          dispatch(taskDetailsActions.businessInitNotifications(10000));
        }
      } catch (error) {
        dispatch(
          globalActions.setSnackBar({
            message: `${error.message}`,
            severity: 'error',
          }),
        );
      }
    }
  };

  const rightActions = React.useMemo<IXGridBulkActionButton[]>(() => {
    return [
      {
        key: 'add-preference-key',
        Icon: AddIcon,
        onClick: handleOpenModal,
        disabled: Authorized(
          [userRoles.recruitment_TeamMember, userRoles.recruitment_Leadership],
          user.userInfo,
        )
          ? loading
          : true,
        tooltipProps: {
          tooltipContent: 'Add Preference',
        },
      },
      {
        key: 'deletePreferences',
        Icon: DeleteOutlinedIcon,
        disabled: enable('deletePreferences'),
        onClick: () => setOpenDeleteDialog(true),
        tooltipProps: {
          tooltipContent: 'Delete Preference',
        },
      },
    ];
  }, [enable, loading, user.userInfo]);

  // TODO: MUI 5 - @Abhishek Wilson, This can be replaced with onColumnResizeStop, onColumnOrderChange, and onColumnVisibilityModelChange to prevent this from being called multiple times
  const handleGridChanges = state => {
    const current = apiRef?.current;
    const currentGridColumns: any = _.cloneDeep(current.getAllColumns());
    if (currentGridColumns && currentGridColumns.length > 0 && currentGridColumns[0].field === '__check__')
      currentGridColumns.shift();
    const fields = _.cloneDeep(current.state.columns.all);
    if (fields.length > 0 && fields[0] === '__check__') fields.shift();
    const columnsEqual = new GridColumns(tableColumns).isEqual(currentGridColumns);
    if (!columnsEqual) {
    }
  };

  return (
    <div>
      <XGrid
        apiRef={apiRef}
        defaultColumns={defaultColumns}
        defaultColumnVisibilityModel={defaultColumnVisibilityModel}
        defaultSortModel={defaultSortModel}
        columns={currentColumns}
        columnVisibilityModel={currentColumnVisibilityModel}
        sortModel={currentSortModel}
        rows={tableData || []}
        loading={loading}
        rowCount={(tableData || []).length}
        autoHeight={true}
        checkboxSelection={Authorized(
          [userRoles.recruitment_TeamMember, userRoles.recruitment_Leadership],
          user.userInfo,
        )}
        onStateChange={handleGridChanges}
        onSortModelChange={onSortModelChanged}
        onColumnVisibilityModelChange={(model, details) => {
          setCurrentColumnVisibilityModel(_.cloneDeep(model));
        }}
        title={'Order Preferences'}
        toolbarProps={{
          rightActions: rightActions,
          refreshButton: true,
          refreshGrid: () => {
            dispatch(
              jobPreferncesActions.getJobPreferencesAction({
                travelerId: parseInt(params.travelerId),
                brandId: parseInt(params.brandId),
              }),
            );
          },
          resetColumns: () => {
            handleResetColumns();
            dispatch(
              globalActions.setSnackBar({
                message: 'Columns have been reset.',
                severity: 'success',
              }),
            );
          },
        }}
      />

      <JobPreferencesModal open={open} title="Add Preference" onClose={() => setOpen(false)} />
      <PreferenceDeleteDialog
        openDeleteDialog={openDeleteDialog}
        onDeleteDialogClose={() => setOpenDeleteDialog(false)}
        deleteJobPreferences={() => deleteJobPreferences()}
        isDeleteInProgress={isDeleteInProgress}
      />
    </div>
  );
};
